import type { RefreshResult } from '../api/auth';
import { useAuthStore } from '../store/authStore';
import { applyAuthSession } from './applyAuthSession';

const REFRESH_LOCK_KEY = 'crowpeaks.auth.refresh-lock';
const REFRESH_RESULT_KEY = 'crowpeaks.auth.refresh-result';
const SESSION_CHANNEL_NAME = 'crowpeaks.auth.session';
const LOCK_STALE_MS = 30_000;
const RESULT_MAX_AGE_MS = 60_000;
const WAIT_FOR_PEER_MS = 15_000;

type SessionPayload = {
  accessToken: string;
  csrfToken: string;
  expiresInSeconds?: number;
  at: number;
};

type RefreshLock = {
  tabId: string;
  since: number;
};

type SessionMessage =
  | { type: 'session-updated'; accessToken: string; csrfToken: string }
  | { type: 'session-refresh-failed' };

const tabId = crypto.randomUUID();

let broadcastChannel: BroadcastChannel | null = null;

function getBroadcastChannel(): BroadcastChannel | null {
  if (typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') {
    return null;
  }

  if (broadcastChannel === null) {
    broadcastChannel = new BroadcastChannel(SESSION_CHANNEL_NAME);
  }

  return broadcastChannel;
}

function parseSessionPayload(raw: string | null): SessionPayload | null {
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as SessionPayload;
    if (
      typeof parsed.accessToken !== 'string'
      || typeof parsed.csrfToken !== 'string'
      || typeof parsed.at !== 'number'
    ) {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

/** Restores a recent access token published by this tab or another tab (avoids redundant /refresh). */
export function tryRestorePublishedSession(): boolean {
  const payload = readPublishedSession();
  if (payload === null) {
    return false;
  }

  applyAuthSession(payload.accessToken, payload.csrfToken, payload.expiresInSeconds);
  return true;
}

function readPublishedSession(): SessionPayload | null {
  if (typeof localStorage === 'undefined') {
    return null;
  }

  const payload = parseSessionPayload(localStorage.getItem(REFRESH_RESULT_KEY));
  if (payload === null || Date.now() - payload.at > RESULT_MAX_AGE_MS) {
    return null;
  }

  return payload;
}

function publishAuthSession(
  accessToken: string,
  csrfToken: string,
  expiresInSeconds?: number
): void {
  const payload: SessionPayload = {
    accessToken,
    csrfToken,
    expiresInSeconds,
    at: Date.now(),
  };

  applyAuthSession(accessToken, csrfToken, expiresInSeconds);

  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(REFRESH_RESULT_KEY, JSON.stringify(payload));
  }

  getBroadcastChannel()?.postMessage({
    type: 'session-updated',
    accessToken,
    csrfToken,
  } satisfies SessionMessage);
}

function publishRefreshFailed(): void {
  getBroadcastChannel()?.postMessage({ type: 'session-refresh-failed' } satisfies SessionMessage);
}

function readRefreshLock(): RefreshLock | null {
  if (typeof localStorage === 'undefined') {
    return null;
  }

  const raw = localStorage.getItem(REFRESH_LOCK_KEY);
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as RefreshLock;
    if (typeof parsed.tabId !== 'string' || typeof parsed.since !== 'number') {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

function tryAcquireRefreshLock(): boolean {
  if (typeof localStorage === 'undefined') {
    return true;
  }

  const now = Date.now();
  const existing = readRefreshLock();

  if (existing !== null && existing.tabId !== tabId && now - existing.since < LOCK_STALE_MS) {
    return false;
  }

  const lock: RefreshLock = { tabId, since: now };
  localStorage.setItem(REFRESH_LOCK_KEY, JSON.stringify(lock));

  const current = readRefreshLock();
  return current?.tabId === tabId;
}

function releaseRefreshLock(): void {
  if (typeof localStorage === 'undefined') {
    return;
  }

  const current = readRefreshLock();
  if (current?.tabId === tabId) {
    localStorage.removeItem(REFRESH_LOCK_KEY);
  }
}

function waitForPeerRefresh(): Promise<SessionPayload | null> {
  if (typeof window === 'undefined') {
    return Promise.resolve(null);
  }

  const existing = readPublishedSession();
  if (existing !== null) {
    return Promise.resolve(existing);
  }

  return new Promise((resolve) => {
    const channel = getBroadcastChannel();

    const finish = (payload: SessionPayload | null) => {
      window.removeEventListener('storage', onStorage);
      channel?.removeEventListener('message', onMessage);
      clearInterval(pollInterval);
      clearTimeout(timeout);
      resolve(payload);
    };

    const onMessage = (event: MessageEvent<SessionMessage>) => {
      const data = event.data;
      if (data?.type === 'session-updated') {
        finish({
          accessToken: data.accessToken,
          csrfToken: data.csrfToken,
          at: Date.now(),
        });
        return;
      }

      if (data?.type === 'session-refresh-failed') {
        finish(null);
      }
    };

    const onStorage = (event: StorageEvent) => {
      if (event.key === REFRESH_RESULT_KEY && event.newValue) {
        const payload = parseSessionPayload(event.newValue);
        if (payload !== null) {
          finish(payload);
        }
        return;
      }

      if (event.key === REFRESH_LOCK_KEY && event.newValue === null) {
        const payload = readPublishedSession();
        if (payload !== null) {
          finish(payload);
        }
      }
    };

    const pollInterval = window.setInterval(() => {
      const payload = readPublishedSession();
      if (payload !== null) {
        finish(payload);
      }
    }, 100);

    const timeout = window.setTimeout(() => finish(null), WAIT_FOR_PEER_MS);

    channel?.addEventListener('message', onMessage);
    window.addEventListener('storage', onStorage);
  });
}

function toRefreshResult(payload: SessionPayload): RefreshResult {
  return {
    success: true,
    accessToken: payload.accessToken,
    csrfToken: payload.csrfToken,
    expiresInSeconds: payload.expiresInSeconds ?? 15 * 60,
  };
}

/**
 * Ensures only one browser tab calls /users/refresh at a time; other tabs reuse the result.
 */
export async function coordinatedTokenRefresh(
  performRefresh: () => Promise<RefreshResult>
): Promise<RefreshResult> {
  if (typeof window === 'undefined') {
    return performRefresh();
  }

  if (!tryAcquireRefreshLock()) {
    const peerResult = await waitForPeerRefresh();
    if (peerResult !== null) {
      applyAuthSession(peerResult.accessToken, peerResult.csrfToken, peerResult.expiresInSeconds);
      return toRefreshResult(peerResult);
    }

    if (!tryAcquireRefreshLock()) {
      const retryPeer = await waitForPeerRefresh();
      if (retryPeer !== null) {
        applyAuthSession(retryPeer.accessToken, retryPeer.csrfToken, retryPeer.expiresInSeconds);
        return toRefreshResult(retryPeer);
      }
    }
  }

  try {
    const result = await performRefresh();

    if (result.success) {
      publishAuthSession(result.accessToken, result.csrfToken, result.expiresInSeconds);
    } else {
      publishRefreshFailed();
    }

    return result;
  } finally {
    releaseRefreshLock();
  }
}

/** Clears in-memory auth state and cross-tab refresh artifacts (not HttpOnly cookies). */
export function clearClientAuthState(): void {
  useAuthStore.getState().clearSession();

  if (typeof localStorage !== 'undefined') {
    localStorage.removeItem(REFRESH_RESULT_KEY);
    localStorage.removeItem(REFRESH_LOCK_KEY);
  }
}

/** Apply tokens refreshed in another tab (cookie + BroadcastChannel / localStorage). */
export function initAuthSessionSync(): () => void {
  if (typeof window === 'undefined') {
    return () => undefined;
  }

  const channel = getBroadcastChannel();

  const onMessage = (event: MessageEvent<SessionMessage>) => {
    const data = event.data;
    if (data?.type === 'session-updated') {
      applyAuthSession(data.accessToken, data.csrfToken);
    }
  };

  const onStorage = (event: StorageEvent) => {
    if (event.key !== REFRESH_RESULT_KEY || !event.newValue) {
      return;
    }

    const payload = parseSessionPayload(event.newValue);
    if (payload !== null && Date.now() - payload.at <= RESULT_MAX_AGE_MS) {
      applyAuthSession(payload.accessToken, payload.csrfToken, payload.expiresInSeconds);
    }
  };

  channel?.addEventListener('message', onMessage);
  window.addEventListener('storage', onStorage);

  return () => {
    channel?.removeEventListener('message', onMessage);
    window.removeEventListener('storage', onStorage);
  };
}
